// =============================================================================
// ui/tabs/CompareAccountsModal.tsx — Coach Me: Compare Accounts.
// -----------------------------------------------------------------------------
// One week, every account, and the question the Accounts tab cannot answer by
// itself: do you BEHAVE differently on one account than another?
//
// ── 🚨 OPENING THIS MUST NEVER SPEND A RUN ──
//
// Every call is billed and burns a per-item allowance the trader cannot get
// back until tomorrow. So the modal opens on a confirmation that says what it
// will do and what it costs, and the request happens on a press. That is not a
// nicety — an effect that fired on mount would bill a trader for opening a
// window, and would empty their allowance on a double render.
//
// ── ⚠ THE ANSWER SURVIVES THE MODAL, THE RUN COUNT SURVIVES THE SESSION ──
//
// Both ride localStorage, keyed by the week. The trader closing the window and
// re-opening it must not have to pay again to re-read what they already bought.
// 🚨 The stored count is ADVISORY — the server holds the real cap, and a cleared
// browser or a second device does not buy more runs. When the two disagree the
// server's `COACH_CAP` is what the screen shows.
//
// ── ⚠ THE MODEL'S TEXT IS RENDERED AS ELEMENTS, NEVER AS MARKUP ──
//
// See `domain/coachProse.ts`. There is no string here that becomes HTML.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSupabase } from '../state/supabase';
import { runFreeformCoach, COACH_RUNS_PER_ITEM } from '../../io/ai/coachRun';
import { AiCoachError } from '../../io/ai/aiCoach';
import { proseBlocks, type ProseBlock, type ProseSpan } from '../../selectors/accountComparison';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface CompareAccountsModalProps {
  /** The prompt, already built. Never rebuilt in here. */
  brief: string;
  /** Distinguishes one week's read from another, for the cache and the cap. */
  cacheKey: string;
  accountCount: number;
  weekStart: string;
  weekEnd: string;
  onClose: () => void;
}

interface Stored { runs: number; text: string; ranAt: string }

const KEY = (cacheKey: string) => `tc.coachrun.${cacheKey}`;

function readStored(cacheKey: string): Stored {
  try {
    const raw = localStorage.getItem(KEY(cacheKey));
    if (!raw) return { runs: 0, text: '', ranAt: '' };
    const v = JSON.parse(raw) as Partial<Stored>;
    return {
      runs: Number(v.runs) || 0,
      text: typeof v.text === 'string' ? v.text : '',
      ranAt: typeof v.ranAt === 'string' ? v.ranAt : '',
    };
  } catch {
    // ⚠ A corrupt entry must not take the modal down. Losing the saved read is
    // an inconvenience; a crash on open is a broken feature.
    return { runs: 0, text: '', ranAt: '' };
  }
}

function writeStored(cacheKey: string, v: Stored): void {
  try { localStorage.setItem(KEY(cacheKey), JSON.stringify(v)); } catch { /* full or blocked */ }
}

type View = 'confirm' | 'running' | 'result' | 'error';

export function CompareAccountsModal(
  { brief, cacheKey, accountCount, weekStart, weekEnd, onClose }: CompareAccountsModalProps,
) {
  const { sb } = useSupabase();
  const [stored, setStored] = useState<Stored>(() => readStored(cacheKey));
  const [view, setView] = useState<View>('confirm');
  const [error, setError] = useState<{ message: string; capped: boolean } | null>(null);

  const runsLeft = Math.max(0, COACH_RUNS_PER_ITEM - stored.runs);

  const run = useCallback(async () => {
    if (!sb) return;
    setView('running');
    setError(null);
    try {
      const res = await runFreeformCoach(sb, brief, cacheKey);
      const next: Stored = {
        runs: stored.runs + 1,
        text: res.text,
        // ⚠ Stamped here rather than in `domain/`, which reads no clock (D26) —
        // the same split every other write in this client uses.
        ranAt: new Date().toISOString(),
      };
      writeStored(cacheKey, next);
      setStored(next);
      setView('result');
    } catch (e) {
      const capped = e instanceof AiCoachError && e.code === 'COACH_CAP';
      if (capped && e instanceof AiCoachError && e.reason === 'item_cap') {
        // 🚨 THE SERVER SAYS THE ALLOWANCE IS GONE, SO THE LOCAL COUNT IS WRONG
        // and must be brought up — otherwise the confirmation keeps offering a
        // Run that can only fail. Happens whenever the runs were spent on
        // another device or the browser was cleared.
        const next = { ...stored, runs: COACH_RUNS_PER_ITEM };
        writeStored(cacheKey, next);
        setStored(next);
      }
      // ⚠ LOGGED HERE AND NOWHERE ELSE — `runFreeformCoach` wraps and rethrows
      // rather than reporting, so this catch is the one place the failure exists.
      setError({
        message: reportFailure('compare accounts coach run', e),
        capped,
      });
      setView('error');
    }
  }, [sb, brief, cacheKey, stored]);

  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Coach Me — Compare Accounts">
      <div className="modal wide cmp-modal">
        <header className="modal-head">
          <b>AI Coach — comparing your accounts</b>
          <button type="button" className="btn" onClick={onClose}>×</button>
        </header>

        <div className="modal-body">
          {view === 'confirm' ? (
            <Confirm
              accountCount={accountCount}
              weekStart={weekStart}
              weekEnd={weekEnd}
              runsLeft={runsLeft}
              hasSaved={!!stored.text}
              onRun={() => void run()}
              onViewSaved={() => setView('result')}
            />
          ) : null}

          {view === 'running' ? (
            <p className="muted cmp-busy" role="status">
              Reading your {accountCount} accounts side by side…
            </p>
          ) : null}

          {view === 'result' && stored.text ? (
            <Result text={stored.text} ranAt={stored.ranAt} runsLeft={runsLeft}
              onRerun={() => void run()} />
          ) : null}

          {view === 'error' && error ? (
            <div className="cmp-error" role="alert">
              <p>{error.message}</p>
              {/* 🚨 SAYS WHETHER IT COST ANYTHING. A failed call is free — the
                  function records a run only on success — and a trader who
                  thinks a network error burned half their allowance will not
                  press the button again. */}
              <p className="muted">
                {error.capped
                  ? 'You can still read your last saved review, or come back tomorrow.'
                  : 'This did not count against your allowance — try again in a moment.'}
              </p>
              <div className="cmp-actions">
                <button type="button" className="btn" onClick={() => setView('confirm')}>Back</button>
                {stored.text ? (
                  <button type="button" className="btn" onClick={() => setView('result')}>
                    Read the last review
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function Confirm(
  { accountCount, weekStart, weekEnd, runsLeft, hasSaved, onRun, onViewSaved }: {
    accountCount: number; weekStart: string; weekEnd: string;
    runsLeft: number; hasSaved: boolean;
    onRun: () => void; onViewSaved: () => void;
  },
) {
  return (
    <div className="cmp-confirm">
      <p>
        An <b>AI Coach review</b> of all <b>{accountCount}</b> of your accounts for{' '}
        <b>{weekStart} → {weekEnd}</b> — where your results differ, and more to the
        point, where your <i>behaviour</i> does.
      </p>
      {/* 🚨 THE SENTENCE THAT STOPS THIS BEING A SURPRISE BILL. */}
      <p>
        It runs <b>only when you ask</b>, so opening this window never spends one.
        You get <b>{COACH_RUNS_PER_ITEM} runs per week</b>.
      </p>
      <p className="muted">
        {runsLeft === COACH_RUNS_PER_ITEM
          ? `Both runs still available this week.`
          : runsLeft > 0
            ? `${runsLeft} of ${COACH_RUNS_PER_ITEM} runs left this week.`
            : `No runs left this week.`}
        {hasSaved ? ' A saved review is available.' : ''}
      </p>
      <div className="cmp-actions">
        {runsLeft > 0 ? (
          <button type="button" className="btn primary" onClick={onRun}>Run the review</button>
        ) : (
          <span className="muted">Your runs for this week are used up.</span>
        )}
        {hasSaved ? (
          <button type="button" className="btn" onClick={onViewSaved}>Read the last review</button>
        ) : null}
      </div>
    </div>
  );
}

function Result(
  { text, ranAt, runsLeft, onRerun }: {
    text: string; ranAt: string; runsLeft: number; onRerun: () => void;
  },
) {
  const blocks = useMemo(() => proseBlocks(text), [text]);
  const when = ranAt
    ? new Date(ranAt).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })
    : '';
  return (
    <div className="cmp-result">
      <div className="cmp-tag">AI Coach · your accounts</div>
      <div className="cmp-prose">
        {blocks.map((b, i) => <Block key={i} b={b} />)}
      </div>
      <div className="cmp-foot">
        <span className="muted">
          {when ? `Reviewed ${when} · ` : ''}
          {runsLeft} of {COACH_RUNS_PER_ITEM} run{runsLeft === 1 ? '' : 's'} left this week
        </span>
        {runsLeft > 0 ? (
          <button type="button" className="btn" onClick={onRerun}>Run it again</button>
        ) : null}
      </div>
    </div>
  );
}

function Spans({ spans }: { spans: readonly ProseSpan[] }) {
  return <>{spans.map((s, i) => (s.bold ? <b key={i}>{s.text}</b> : <span key={i}>{s.text}</span>))}</>;
}

function Block({ b }: { b: ProseBlock }) {
  if (b.kind === 'heading') {
    // ⚠ Always an h4 or lower. The modal's own title is the h-level above, and
    // a model emitting `# Something` must not outrank the window it is in.
    const Tag = (b.level === 1 ? 'h4' : b.level === 2 ? 'h5' : 'h6') as 'h4' | 'h5' | 'h6';
    return <Tag className="cmp-h"><Spans spans={b.spans} /></Tag>;
  }
  if (b.kind === 'list') {
    return (
      <ul className="cmp-list">
        {b.items.map((it, i) => <li key={i}><Spans spans={it} /></li>)}
      </ul>
    );
  }
  return <p className="cmp-p"><Spans spans={b.spans} /></p>;
}
